import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  adminNavigationFooter,
  adminNavigationSections,
} from '@/config/adminNavigation';
import { useAuthorization } from '@/authorization';
import { getAdminModules } from '@/config/adminModules';
import type { AdminModuleDefinition } from '@/types/adminModule';

interface AdminNavigationProps {
  selectedModuleId: string;
  onSelect: (moduleId: string) => void;
}

export function AdminNavigation({
  selectedModuleId,
  onSelect,
}: AdminNavigationProps) {
  const { t } = useTranslation('entity-portal');
  const { hasPermission, isLoading } = useAuthorization();

  const modulesById = useMemo(() => {
    return new Map(getAdminModules().map((module) => [module.id, module]));
  }, []);

  const sections = useMemo(() => {
    return [...adminNavigationSections]
      .sort((left, right) => left.sortOrder - right.sortOrder)
      .map((section) => ({
        ...section,
        modules: section.moduleIds
          .map((moduleId) => modulesById.get(moduleId))
          .filter(
            (module): module is AdminModuleDefinition =>
              Boolean(module) &&
              (isLoading ||
                !module?.permission ||
                hasPermission(module.permission)),
          ),
      }))
      .filter((section) => section.modules.length > 0);
  }, [hasPermission, isLoading, modulesById]);

  return (
    <Box
      sx={{
        py: 1.5,
        px: 1,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {sections.map((section) => (
          <List
            key={section.id}
            dense
            disablePadding
            component="nav"
            aria-label={t(section.labelKey)}
            subheader={
              <ListSubheader component="div" sx={{ py: 1, px: 1.5 }}>
                {t(section.labelKey)}
              </ListSubheader>
            }
          >
            {section.modules.map((module) => {
              const Icon = module.icon;
              const isSelected = module.id === selectedModuleId;

              return (
                <ListItemButton
                  key={module.id}
                  selected={isSelected}
                  onClick={() => onSelect(module.id)}
                  sx={{
                    borderRadius: 1.5,
                    mx: 0.75,
                    mb: 0.25,
                    py: 0.875,
                    '&.Mui-selected': {
                      bgcolor: 'action.selected',
                      color: 'primary.main',
                      '&:hover': {
                        bgcolor: 'action.selected',
                      },
                      '& .MuiListItemIcon-root': {
                        color: 'primary.main',
                      },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={t(module.nameKey)}
                    primaryTypographyProps={{
                      variant: isSelected ? 'subtitle1' : 'subtitle2',
                      noWrap: true,
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        ))}
      </Box>

      <Box sx={{ px: 1.5, py: 1.5, mt: 'auto' }}>
        <Typography variant="caption" color="text.disabled" display="block">
          {t(adminNavigationFooter.nodeLabelKey, {
            node: adminNavigationFooter.nodeValue,
          })}
        </Typography>
        <Typography variant="caption" color="text.disabled" display="block">
          {t(adminNavigationFooter.versionLabelKey, {
            version: adminNavigationFooter.versionValue,
          })}
        </Typography>
      </Box>
    </Box>
  );
}
